import {
  TEstimateItem,
  TEstimateBaseItem,
  TCopyTo
} from '../types/estimate/estimate';
import { v4 as uuidv4 } from 'uuid';
import {
  calcAllSellingPrice,
  calcAllCostPrice,
  calcGrossProfit,
  calcGrossProfitMargin,
  sumAllCostPrices,
  sumAllSellingPrices,
  updateParentsNumericalValues,
  setGrossProfitMarginTo100,
  updateSellingPricesFromGrossProfitMargin
} from '../utils/calcRules';
import { AutoFixHighRounded } from '@mui/icons-material';

/**
 * number[]内を合計し、numberを返却
 */
export const reduceArr = (arr: number[]) => {
  return arr.reduce((sum, element) => {
    return sum + element;
  }, 0);
};

/**
 * ドラッグアンドドロップ入れ替え関数
 */
export const reorder = (list: any, startIndex: any, endIndex: any) => {
  const result = Array.from(list);
  const [removed] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
};

/**
 * 小数点切り捨て
 * @param number
 * @param decimals
 * @returns
 */
export function roundDown(number: any, decimals: any) {
  decimals = decimals || 0;
  return Math.floor(number * Math.pow(10, decimals)) / Math.pow(10, decimals);
}

/**
 * ステータスからエクセル出力用の文字列を取得
 */
export function getEstimateStatusString(status: number) {
  switch (status) {
    case 1:
      return '作成中';
    case 2:
      return '変更';
    case 3:
      return '売上予定';
    case 4:
      return '受注';
    case 5:
      return '失注';
    default:
      console.log('status not found');
      break;
  }
}

// 見積書のバリデーション
// 文字数制限、行数制限を引数として受け取る
export const handleTextValidation = (
  text: string,
  maxChars: number,
  maxLines: number | null = null,
  maxLineChars: number | null = null,
  callback: ((cleanText: string) => void) | null = null,
  fieldName: string | null = null,
  isComposing = false
) => {
  if (isComposing) return;

  const rawText = text;
  let cleanText = '';
  let charCount = 0; // 全体の文字数カウント
  let lineCharCount = 0; // 各行の文字数カウント
  let lineCount = 1; // 行数カウント

  for (let i = 0; i < rawText.length; i++) {
    // 文字が改行コードかチェック
    if (rawText[i] === '\n' || rawText[i] === '\r') {
      // Windowsの改行コード（\r\n）を考慮
      if (rawText[i] === '\r' && rawText[i + 1] === '\n') {
        i++;
      }
      lineCount++;
      lineCharCount = 0; // 各行の文字数カウントをリセット

      if (maxLines !== null && lineCount > maxLines) {
        alert(`${maxLines}行まで入力可能です`);
        break;
      }
      cleanText += rawText[i];
    } else {
      charCount++;
      lineCharCount++;

      // 1行の文字数制限を超えた場合、自動的に改行を挿入し、次の行に文字を入れる
      if (maxLineChars !== null && lineCharCount > maxLineChars) {
        cleanText += '\n';
        lineCount++;
        lineCharCount = 1;
      }

      if (maxLines !== null && lineCount > maxLines) {
        alert(`${maxLines}行まで入力可能です`);
        break;
      }
      // 全体の文字数制限
      if (charCount > maxChars) {
        if (fieldName) {
          alert(`${fieldName}は${maxChars}文字まで入力可能です`);
        } else {
          alert(`${maxChars}文字まで入力可能です`);
        }
        break;
      }
      cleanText += rawText[i];
    }
  }

  // cleanTextをコールバック関数に渡す
  if (callback) {
    callback(cleanText);
  }
};

/**
 * 見積マスタの仕様、備考、社内用メモでのバリデーション
 */
export const validateTextareaInput = (
  key: 'spec' | 'remarks' | 'author_memo',
  text: string,
  callback: ((cleanText: string) => void) | null = null
) => {
  const rawText = text;
  let cleanText = '';
  let charCount = 0;
  let lineCount = 1;
  let newlineInserted = false;
  let existingNewlineIndex = rawText.search(/\r\n|\n|\r/);

  for (let i = 0; i < rawText.length; i++) {
    if (rawText[i] === '\n' || rawText[i] === '\r') {
      if (rawText[i] === '\r' && rawText[i + 1] === '\n') {
        i++;
      }
      if (newlineInserted) {
        // 既に改行が挿入されている場合の警告
        if (i > existingNewlineIndex) {
          alert('2行以内で入力してください');
        } else {
          alert('1行20文字以内で入力してください');
        }
        break;
      } else if (charCount > 20) {
        // 1行目が20文字を超えている場合
        alert('1行20文字以内で入力してください');
        break;
      }
      newlineInserted = true;
      existingNewlineIndex = i;
      charCount = 0;
      lineCount++;
    } else {
      charCount++;
    }
    // 改行後の行で20文字を超えた場合、入力を停止
    if (newlineInserted && charCount > 20) {
      alert('1行20文字以内で入力してください');
      break;
    }

    if (charCount > 40) break;
    cleanText += rawText[i];
  }
  if (callback) {
    callback(cleanText);
  }
};

/**
 * IDから単位を取得
 */
export const findMUnitById: (
  mUnitsData: { id: number; name: string }[],
  id: number
) => { id: number; name: string } = (mUnitsData, id) => {
  return mUnitsData.find(unit => unit.id === id) || { id: 0, name: '' };
};

/**
 * エクセルエクスポート用のデータ作成
 */
export function createExcelExportFormat(
  documentNumber: string, // 見積書番号
  statusString: string, // ステータス
  salesUserName: string, // 営業担当
  accumulationUserName: string, // 積算担当
  SubmitDate: string, // 見積提出日
  constructSub: string, // 工事件名
  constructDays: any, // 工事日数
  clientName: string, // 請求先顧客名
  estimateConditions: string, // 御見積条件
  contractorName: string, // 契約者名
  remarks: string, // 摘要
  createdUser: string, // 作成者
  createdAt: string, // 作成者日
  updatedUser: string, // 修正者
  updatedAt: string, // 修正者
  totalPrice: number, // 見積合計金額
  estimateItemList: TEstimateBaseItem[] // 明細
) {
  const data_main = [
    {
      見積書番号: documentNumber,
      ステータス: statusString,
      営業担当: salesUserName,
      積算担当: accumulationUserName,
      見積提出日: SubmitDate,
      御見積条件: estimateConditions,
      工事件名: constructSub,
      工事日数: constructDays ? constructDays : constructDays === 0 ? 0 : '',
      請求先顧客名: clientName,
      契約者名: contractorName,
      摘要: remarks,
      作成者: `${createdUser}-${createdAt}`,
      修正者: `${updatedUser}-${updatedAt}`,
      見積合計金額: totalPrice
    }
  ];

  const data_items: any = [];
  estimateItemList.forEach((item: TEstimateBaseItem) => {
    const itemObj: any = {
      第一階層: item.name,
      第二階層: '',
      第三階層: '',
      第四階層: '',
      第五階層: '',
      仕様: item.spec,
      数量: item.quantity ? item.quantity : item.quantity === 0 ? 0 : '',
      単位: item.unitName ? item.unitName : '',
      単価: item.hasUnderLayerFlg
        ? ''
        : item.sellingUnitPrice
        ? item.sellingUnitPrice
        : 0,
      原価数量: item.costQuantity
        ? item.costQuantity
        : item.costQuantity === 0
        ? 0
        : '',
      原価単位: item.costUnitName ? item.costUnitName : '',
      原価単価: item.hasUnderLayerFlg
        ? ''
        : item.costUnitPrice
        ? item.costUnitPrice
        : 0,
      定価金額: item.allSellingPrice,
      原価金額: item.allCostPrice,
      粗利: item.grossProfit,
      粗利率: item.grossProfitMargin,
      備考: item.remarks,
      社内用メモ: item.author_memo
    };
    data_items.push(itemObj);
    if (item.hasUnderLayerFlg && item.childItems.length > 0) {
      item.childItems.forEach((secondItem: TEstimateBaseItem) => {
        const secondObj: any = {
          第一階層: '',
          第二階層: secondItem.name,
          第三階層: '',
          第四階層: '',
          第五階層: '',
          仕様: secondItem.spec,
          数量: secondItem.quantity
            ? secondItem.quantity
            : secondItem.quantity === 0
            ? 0
            : '',
          単位: secondItem.unitName ? secondItem.unitName : '',
          単価: secondItem.hasUnderLayerFlg
            ? ''
            : secondItem.sellingUnitPrice
            ? secondItem.sellingUnitPrice
            : 0,
          原価数量: secondItem.costQuantity
            ? secondItem.costQuantity
            : secondItem.costQuantity === 0
            ? 0
            : '',
          原価単位: secondItem.costUnitName ? secondItem.costUnitName : '',
          原価単価: secondItem.hasUnderLayerFlg
            ? ''
            : secondItem.costUnitPrice
            ? secondItem.costUnitPrice
            : 0,
          定価金額: secondItem.allSellingPrice,
          原価金額: secondItem.allCostPrice,
          粗利: secondItem.grossProfit,
          粗利率: secondItem.grossProfitMargin,
          備考: secondItem.remarks
        };
        data_items.push(secondObj);
        if (secondItem.hasUnderLayerFlg && secondItem.childItems.length > 0) {
          secondItem.childItems.forEach((thirdItem: TEstimateBaseItem) => {
            const thirdObj: any = {
              第一階層: '',
              第二階層: '',
              第三階層: thirdItem.name,
              第四階層: '',
              第五階層: '',
              仕様: thirdItem.spec,
              数量: thirdItem.quantity
                ? thirdItem.quantity
                : thirdItem.quantity === 0
                ? 0
                : '',
              単位: thirdItem.unitName ? thirdItem.unitName : '',
              単価: thirdItem.hasUnderLayerFlg
                ? ''
                : thirdItem.sellingUnitPrice
                ? thirdItem.sellingUnitPrice
                : 0,
              原価数量: thirdItem.costQuantity
                ? thirdItem.costQuantity
                : thirdItem.costQuantity === 0
                ? 0
                : '',
              原価単位: thirdItem.costUnitName ? thirdItem.costUnitName : '',
              原価単価: thirdItem.hasUnderLayerFlg
                ? ''
                : thirdItem.costUnitPrice
                ? thirdItem.costUnitPrice
                : 0,
              定価金額: thirdItem.allSellingPrice,
              原価金額: thirdItem.allCostPrice,
              粗利: thirdItem.grossProfit,
              粗利率: thirdItem.grossProfitMargin,
              備考: thirdItem.remarks
            };
            data_items.push(thirdObj);
            if (thirdItem.hasUnderLayerFlg && thirdItem.childItems.length > 0) {
              thirdItem.childItems.forEach((fourthItem: TEstimateBaseItem) => {
                const fourthObj: any = {
                  第一階層: '',
                  第二階層: '',
                  第三階層: '',
                  第四階層: fourthItem.name,
                  第五階層: '',
                  仕様: fourthItem.spec,
                  数量: fourthItem.quantity
                    ? fourthItem.quantity
                    : fourthItem.quantity === 0
                    ? 0
                    : '',
                  単位: fourthItem.unitName ? fourthItem.unitName : '',
                  単価: fourthItem.hasUnderLayerFlg
                    ? ''
                    : fourthItem.sellingUnitPrice
                    ? fourthItem.sellingUnitPrice
                    : 0,
                  原価数量: fourthItem.costQuantity
                    ? fourthItem.costQuantity
                    : fourthItem.costQuantity === 0
                    ? 0
                    : '',
                  原価単位: fourthItem.costUnitName
                    ? fourthItem.costUnitName
                    : '',
                  原価単価: fourthItem.hasUnderLayerFlg
                    ? ''
                    : fourthItem.costUnitPrice
                    ? fourthItem.costUnitPrice
                    : 0,
                  定価金額: fourthItem.allSellingPrice,
                  原価金額: fourthItem.allCostPrice,
                  粗利: fourthItem.grossProfit,
                  粗利率: fourthItem.grossProfitMargin,
                  備考: fourthItem.remarks
                };
                data_items.push(fourthObj);
                if (
                  fourthItem.hasUnderLayerFlg &&
                  fourthItem.childItems.length > 0
                ) {
                  fourthItem.childItems.forEach(
                    (fifthItem: TEstimateBaseItem) => {
                      const fifthObj: any = {
                        第一階層: '',
                        第二階層: '',
                        第三階層: '',
                        第四階層: '',
                        第五階層: fifthItem.name,
                        仕様: fifthItem.spec,
                        数量: fifthItem.quantity
                          ? fifthItem.quantity
                          : fifthItem.quantity === 0
                          ? 0
                          : '',
                        単位: fifthItem.unitName ? fifthItem.unitName : '',
                        単価: fifthItem.hasUnderLayerFlg
                          ? ''
                          : fifthItem.sellingUnitPrice
                          ? fifthItem.sellingUnitPrice
                          : 0,
                        原価数量: fifthItem.costQuantity
                          ? fifthItem.costQuantity
                          : fifthItem.costQuantity === 0
                          ? 0
                          : '',
                        原価単位: fifthItem.costUnitName
                          ? fifthItem.costUnitName
                          : '',
                        原価単価: fifthItem.hasUnderLayerFlg
                          ? ''
                          : fifthItem.costUnitPrice
                          ? fifthItem.costUnitPrice
                          : 0,
                        定価金額: fifthItem.allSellingPrice,
                        原価金額: fifthItem.allCostPrice,
                        粗利: fifthItem.grossProfit,
                        粗利率: fifthItem.grossProfitMargin,
                        備考: fifthItem.remarks
                      };
                      data_items.push(fifthObj);
                    }
                  );
                }
              });
            }
          });
        }
      });
    }
  });

  return { data_main, data_items };
}

/**
 *  コピーする項目のidをユニークなものに更新する
 */
export function updateCopyItemIds(item: TEstimateItem): TEstimateItem {
  return {
    ...item,
    id: uuidv4(), // 新しいUUIDを割り当てる
    childItems: item.childItems ? item.childItems.map(updateCopyItemIds) : []
  };
}

/**
 * 見積書項目の階層を変更する
 * 子項目が5階層を超える場合は、5階層に収める
 */
export function updateEstimateItemLayer(
  item: TEstimateItem,
  targetLayer: number
): TEstimateItem {
  return {
    ...item,
    layer: targetLayer,
    childItems:
      item.childItems.length > 0 && targetLayer <= 5
        ? item.childItems.map(child =>
            updateEstimateItemLayer(child, targetLayer + 1)
          )
        : []
  };
}

/**
 * 階層を指定して、コピー先一覧を取得する
 * @param estimateItemList 見積書項目のリスト
 * @param layer コピー先の階層
 * @returns コピー先一覧
 */
export const getCopyToList = (
  estimateItemList: TEstimateItem[],
  layer: number
): TCopyTo[] => {
  if (layer === 0) {
    return [];
  }
  let currentLayer = 1;
  let result = estimateItemList.reduce(
    (acc, item, index) => [...acc, { location: [{ index, item }] }],
    [] as TCopyTo[]
  );
  while (currentLayer < layer) {
    result = result.flatMap(copyTo =>
      copyTo.location.at(-1)!.item.childItems.map((child, index) => {
        return { location: [...copyTo.location, { index, item: child }] };
      })
    );
    currentLayer += 1;
  }
  return result;
};

/**
 * インデックスのリストを辿って見積書項目を取得する
 * @param estimateItemList 見積書項目のリスト
 * @param indexes 欲しい項目に辿り着くためのインデックスのリスト
 * @returns 見積書項目
 */
export function getEstimateItemByIndexes(
  estimateItemList: TEstimateItem[],
  indexes: number[]
): TEstimateItem {
  let result = estimateItemList[indexes[0]];
  indexes.forEach((value, index) => {
    if (index !== 0) result = result.childItems[value];
  });

  return result;
}

/**
 * インデックスのリストを辿って親項目に到達し、その子階層を取得する.
 * indexesが空の場合は、そのまま見積書項目を返す
 * @param estimateItemList 見積書項目のリスト
 * @param indexes 親項目に到達するためのインデックスのリスト
 * @returns 見積書項目のリスト
 */
export function getChildrenByIndexes(
  estimateItemList: TEstimateItem[],
  indexes: number[]
): TEstimateItem[] {
  if (indexes.length === 0) {
    return estimateItemList;
  }

  const parent = getEstimateItemByIndexes(estimateItemList, indexes);
  return parent.childItems;
}

/**
 * インデックスのリストを辿って見積書項目を取得する
 * @param estimateItemList 見積書項目のリスト
 * @param indexes 欲しい項目に辿り着くためのインデックスのリスト
 * @returns インデックスに沿った見積書項目
 */
export function getEstimateItemsByIndexes(
  estimateItemList: TEstimateItem[],
  indexes: readonly number[]
): TEstimateItem[] {
  let result = [];
  let current = estimateItemList[indexes[0]];
  result.push(current);
  indexes.forEach((value, index) => {
    if (index !== 0) {
      current = current.childItems[value];
      result.push(current);
    }
  });

  return result;
}

export function instantiateEstimateItemFromBase(
  id: number | string,
  base: TEstimateBaseItem,
  childItems: TEstimateItem[]
): TEstimateItem {
  let result: any = {};
  result.id = id;
  result.name = base.name;
  result.spec = base.spec;
  result.quantity = base.quantity;
  result.costQuantity = base.cost_quantity;
  result.unitId = base.unit_id;
  result.costUnitId = base.cost_unit_id;
  result.sellingUnitPrice = base.selling_unit_price
    ? base.selling_unit_price
    : 0;
  result.costUnitPrice = base.cost_unit_price ? base.cost_unit_price : 0;
  result.unitName = base.unit_name;
  result.costUnitName = base.cost_unit_name;
  result.remarks = base.remarks;
  result.author_memo = base.author_memo;
  result.layer = base.layer;
  result.hasUnderLayerFlg = base.has_under_layer_flg;
  result.childItems = childItems;

  // 子階層がある場合は単価・原価単価を更新
  if (childItems.length > 0) {
    base.sellingUnitPrice = sumAllSellingPrices(childItems);
    base.costUnitPrice = sumAllCostPrices(childItems);
  }

  result.allSellingPrice = calcAllSellingPrice(
    Number(base.selling_unit_price),
    Number(result.quantity)
  );
  result.allCostPrice = calcAllCostPrice(
    Number(base.cost_unit_price),
    Number(result.costQuantity)
  );
  result.grossProfit = calcGrossProfit(
    result.allSellingPrice,
    result.allCostPrice
  );
  result.grossProfitMargin =
    result.allSellingPrice !== 0
      ? calcGrossProfitMargin(result.allSellingPrice, result.allCostPrice)
      : 0;
  return result;
}

export function createDefaultEstimateItems(
  layer: number,
  itemCount: number
): TEstimateItem[] {
  return Array(itemCount)
    .fill(null)
    .map(() => ({
      name: '',
      spec: '',
      quantity: '',
      costQuantity: '',
      unitId: 0,
      unitName: '',
      costUnitName: '',
      costUnitId: 0,
      sellingUnitPrice: '',
      costUnitPrice: '',
      allSellingPrice: 0,
      allCostPrice: 0,
      grossProfit: 0,
      grossProfitMargin: 0,
      remarks: '',
      author_memo: '',
      hasUnderLayerFlg: false,
      childItems: [],
      id: uuidv4(),
      layer: layer
    }));
}
